import { getNamespacedResource } from "SharedStores/util";
import {
    getNewEnvelopeItem,
    getNewEnvelopeRecipient,
    getNewEnvelopeFieldMapping
} from "./clientCreatableEnvelopeObjects";
import { PROGRAM_TYPES } from "SharedStores/Program/programStore";
import { DOCUMENT_TEMPLATE_TYPES } from "DocumentTemplates/documentTemplateStore";
import { DIALOG_HOOK, DIALOG_RESULT } from "Components/pc-dialog.vue";
import { TEMPLATE_TAG_TYPE_CONSTANT, SIGNATORY_DATA_TARGET_TYPE } from "TemplateTags/templateTagTypes";
import { DATA_FIELD_TYPE, DATA_FIELD_GROUP_TYPE } from "Types/dataFieldTypes";
import { ALERT_SEVERITY, ALERT_DURATION } from "Util/alerts";
import { VALIDATOR_TYPES, getValidator } from "Validation/validator";

import { numericCompare } from "Util/util";

import { KEYS, NAMESPACE } from "./i18n/editEnvelope.i18n";
import { KEYS as COMMON_KEYS, NAMESPACE as COMMON_NAMESPACE } from "I18n/common.i18n";

const EDIT_ENVELOPE_NAMESPACE = "editEnvelope";

// Getters
// Getters that return a function are prefixed with "get". Getters that return a value which can
// be cached are not.
const ITEM_LIST = "itemList";
const ORDERED_ITEM_IDS = "orderedItemIds";
const PROVIDER_CAPABILITIES = "providerCapabilities";
const GET_ITEM_BY_ID = "getItemById";
const GET_ITEM_SOURCE_ID_BY_ITEM_ID = "getItemSourceIdByItemId";
const RECIPIENT_LIST = "recipientList";
const ORDERED_RECIPIENT_IDS = "orderedRecipientIds";
const RECIPIENT_LABEL_SET = "recipientLabelSet";
const GET_RECIPIENT_BY_ID = "getRecipientById";
const GET_RECIPIENT_BY_LABEL = "getRecipientByLabel";
const GET_AVAILABLE_ATTACHMENT_DEFINITIONS = "getAvailableAttachmentDefinitions";
const ITEMS_BY_RECIPIENT_ID = "itemsByRecipientId";
const GET_RECIPIENTS_EXCLUSIVE_TO_ITEM = "getRecipientsExclusiveToItem";
const MAPPING_LIST = "mappingList";
const GET_MAPPING_BY_ID = "getMappingById";
const GET_MAPPINGS_BY_ITEM_ID = "getMappingsByItemId";
const GET_MAPPING_TARGET_OPTIONS_BY_MAPPING_ID = "getMappingTargetOptionsByMappingId";
const ENVELOPE_VALIDATION = "envelopeValidation";
const SHOW_ENVELOPE_ERRORS = "showEnvelopeErrors";
const IS_FIXED_RECIPIENT_NAME_VALID = "isFixedRecipientNameValid";
const IS_FIXED_RECIPIENT_EMAIL_LENGTH_VALID = "isFixedRecipientEmailLengthValid";
const IS_FIXED_RECIPIENT_EMAIL_FORMAT_VALID = "isFixedRecipientEmailFormatValid";
const GET_ENVELOPE_RECIPIENT_VALID = "getEnvelopeRecipientValid";
const GET_RECIPIENTS_BY_ITEM_ID = "getRecipientsByItemId";
const GET_UNAPPLIED_MAPPINGS_BY_ITEM_ID = "getUnappliedMappingsByItemId";
const IS_INDIVIDUAL_FIELDS_RECIPIENT_CONTACT_INFO_VALID = "isIndividualFieldsRecipientContactInfoValid";

// Mutations
const SET_ENVELOPE_NAME = "setEnvelopeName";
const SET_ENVELOPE_DESCRIPTION = "setEnvelopeDescription";
const SET_ENVELOPE_EMAIL_SUBJECT = "setEnvelopeEmailSubject";
const SET_ENVELOPE_EMAIL_MESSAGE = "setEnvelopeEmailMessage";
const SET_ROUTING_ENABLED = "setRoutingEnabled";
const SET_ALLOWS_WET_SIGN = "setAllowsWetSign";
const SET_ENVELOPE_TARGET_ATTACHMENT = "setEnvelopeTargetAttachment";
const SET_ENVELOPE_CERTIFICATE_OF_COMPLETION = "setEnvelopeCertificateOfCompletion";
const SET_ITEM_INCLUSION_FORMULA = "setItemInclusionFormula";
const SET_ITEM_TARGET_ATTACHMENT = "setItemTargetAttachment";
const SET_ATTACHMENT_NAMES_INCLUDE_PROJECT_NUMBER = "setAttachmentNamesIncludeProjectNumber";
const SET_ALL_ITEM_ORDERS = "setAllItemOrders";
const SET_ITEM_IS_REQUIRED = "setItemIsRequired";
const SET_RECIPIENT_ROUTING_ORDER = "setRecipientRoutingOrder";
const SET_RECIPIENT_ROUTING_ORDER_MULTIPLE = "setRecipientRoutingOrderMultiple";
const SET_RECIPIENT_PRIVATE_MESSAGE = "setRecipientPrivateMessage";
const SET_RECIPIENT_CONTACT_INFO_TYPE = "setRecipientContactInfoType";
const SET_MAPPING_DATA_TARGET = "setMappingDataTarget";
const SET_ITEM_LABEL = "setItemLabel";
const SET_SHOW_ENVELOPE_ERRORS = "setShowEnvelopeErrors";
const SET_MAPPING_FORMAT = "setMappingFormat";
const SET_MAPPING_IS_REQUIRED = "setMappingIsRequired";

// private Mutations
const _INSERT_NEW_ITEM = "_insertNewItem";
const _REMOVE_ITEM = "_removeItem";
const _INSERT_NEW_RECIPIENT = "_insertNewRecipient";
const _COMMIT_RECIPIENT_CONTACT_INFO_SOURCE = "_commitRecipientContactInfoSource";
const _SET_SINGLE_RECIPIENT_ROUTING_ORDER = "_setSingleRecipientRoutingOrder";
const _SET_MULTIPLE_RECIPIENT_ROUTING_ORDERS = "_setMultipleRecipientRoutingOrders";
const _INSERT_NEW_MAPPING = "_insertNewMapping";
const _REMOVE_MAPPING = "_removeMapping";
const _COMMIT_REMOVE_RECIPIENT = "_commitRemoveRecipient";
const _SET_ITEM_ATTACHMENT_DEFINITION_ID = "_setItemAttachmentDefinitionId";
const _SET_ITEM_CONTENT_ITEM_ID = "_setItemContentItemId";
const _SET_ITEM_DOCUMENT_TEMPLATE_DEFINITION_ID = "_setItemDocumentTemplateDefinitionId";

// Actions
const OPEN_EDIT_ITEM_DIALOG = "openEditItemDialog";
const OPEN_REMOVE_ITEM_DIALOG = "openRemoveItemDialog";
const ADD_ITEM = "addItem";
const REMOVE_ITEM = "removeItem";
const SET_RECIPIENT_CONTACT_FIELD = "setRecipientContactField";
const INITIALIZE_ALL_ITEM_ORDERS = "initializeAllItemOrders";
const INITIALIZE_ITEM_MAPPINGS = "initializeItemMappings";
const SANITIZE_ENVELOPE_FOR_SAVE = "sanitizeEnvelopeForSave";
const APPLY_MAPPING_DEFAULTS = "applyMappingDefaults";

const EDIT_ENVELOPE_TYPES = {
    getters: {
        [ITEM_LIST]: getNamespacedResource(EDIT_ENVELOPE_NAMESPACE, ITEM_LIST),
        [ORDERED_ITEM_IDS]: getNamespacedResource(EDIT_ENVELOPE_NAMESPACE, ORDERED_ITEM_IDS),
        [PROVIDER_CAPABILITIES]: getNamespacedResource(EDIT_ENVELOPE_NAMESPACE, PROVIDER_CAPABILITIES),
        [GET_ITEM_BY_ID]: getNamespacedResource(EDIT_ENVELOPE_NAMESPACE, GET_ITEM_BY_ID),
        [GET_ITEM_SOURCE_ID_BY_ITEM_ID]: getNamespacedResource(EDIT_ENVELOPE_NAMESPACE, GET_ITEM_SOURCE_ID_BY_ITEM_ID),
        [RECIPIENT_LIST]: getNamespacedResource(EDIT_ENVELOPE_NAMESPACE, RECIPIENT_LIST),
        [ORDERED_RECIPIENT_IDS]: getNamespacedResource(EDIT_ENVELOPE_NAMESPACE, ORDERED_RECIPIENT_IDS),
        [RECIPIENT_LABEL_SET]: getNamespacedResource(EDIT_ENVELOPE_NAMESPACE, RECIPIENT_LABEL_SET),
        [GET_RECIPIENT_BY_ID]: getNamespacedResource(EDIT_ENVELOPE_NAMESPACE, GET_RECIPIENT_BY_ID),
        [GET_RECIPIENT_BY_LABEL]: getNamespacedResource(EDIT_ENVELOPE_NAMESPACE, GET_RECIPIENT_BY_LABEL),
        [GET_AVAILABLE_ATTACHMENT_DEFINITIONS]: getNamespacedResource(
            EDIT_ENVELOPE_NAMESPACE,
            GET_AVAILABLE_ATTACHMENT_DEFINITIONS
        ),
        [ITEMS_BY_RECIPIENT_ID]: getNamespacedResource(EDIT_ENVELOPE_NAMESPACE, ITEMS_BY_RECIPIENT_ID),
        [GET_RECIPIENTS_EXCLUSIVE_TO_ITEM]: getNamespacedResource(
            EDIT_ENVELOPE_NAMESPACE,
            GET_RECIPIENTS_EXCLUSIVE_TO_ITEM
        ),
        [MAPPING_LIST]: getNamespacedResource(EDIT_ENVELOPE_NAMESPACE, MAPPING_LIST),
        [GET_MAPPING_BY_ID]: getNamespacedResource(EDIT_ENVELOPE_NAMESPACE, GET_MAPPING_BY_ID),
        [GET_MAPPINGS_BY_ITEM_ID]: getNamespacedResource(EDIT_ENVELOPE_NAMESPACE, GET_MAPPINGS_BY_ITEM_ID),
        [GET_MAPPING_TARGET_OPTIONS_BY_MAPPING_ID]: getNamespacedResource(
            EDIT_ENVELOPE_NAMESPACE,
            GET_MAPPING_TARGET_OPTIONS_BY_MAPPING_ID
        ),
        [ENVELOPE_VALIDATION]: getNamespacedResource(EDIT_ENVELOPE_NAMESPACE, ENVELOPE_VALIDATION),
        [SHOW_ENVELOPE_ERRORS]: getNamespacedResource(EDIT_ENVELOPE_NAMESPACE, SHOW_ENVELOPE_ERRORS),
        [IS_FIXED_RECIPIENT_NAME_VALID]: getNamespacedResource(EDIT_ENVELOPE_NAMESPACE, IS_FIXED_RECIPIENT_NAME_VALID),
        [IS_FIXED_RECIPIENT_EMAIL_LENGTH_VALID]: getNamespacedResource(
            EDIT_ENVELOPE_NAMESPACE,
            IS_FIXED_RECIPIENT_EMAIL_LENGTH_VALID
        ),
        [IS_FIXED_RECIPIENT_EMAIL_FORMAT_VALID]: getNamespacedResource(
            EDIT_ENVELOPE_NAMESPACE,
            IS_FIXED_RECIPIENT_EMAIL_FORMAT_VALID
        ),
        [GET_ENVELOPE_RECIPIENT_VALID]: getNamespacedResource(EDIT_ENVELOPE_NAMESPACE, GET_ENVELOPE_RECIPIENT_VALID),
        [GET_RECIPIENTS_BY_ITEM_ID]: getNamespacedResource(EDIT_ENVELOPE_NAMESPACE, GET_RECIPIENTS_BY_ITEM_ID),
        [GET_UNAPPLIED_MAPPINGS_BY_ITEM_ID]: getNamespacedResource(
            EDIT_ENVELOPE_NAMESPACE,
            GET_UNAPPLIED_MAPPINGS_BY_ITEM_ID
        ),
        [IS_INDIVIDUAL_FIELDS_RECIPIENT_CONTACT_INFO_VALID]: getNamespacedResource(
            EDIT_ENVELOPE_NAMESPACE,
            IS_INDIVIDUAL_FIELDS_RECIPIENT_CONTACT_INFO_VALID
        )
    },
    mutations: {
        [SET_ENVELOPE_NAME]: getNamespacedResource(EDIT_ENVELOPE_NAMESPACE, SET_ENVELOPE_NAME),
        [SET_ENVELOPE_DESCRIPTION]: getNamespacedResource(EDIT_ENVELOPE_NAMESPACE, SET_ENVELOPE_DESCRIPTION),
        [SET_ENVELOPE_EMAIL_SUBJECT]: getNamespacedResource(EDIT_ENVELOPE_NAMESPACE, SET_ENVELOPE_EMAIL_SUBJECT),
        [SET_ENVELOPE_EMAIL_MESSAGE]: getNamespacedResource(EDIT_ENVELOPE_NAMESPACE, SET_ENVELOPE_EMAIL_MESSAGE),
        [SET_ROUTING_ENABLED]: getNamespacedResource(EDIT_ENVELOPE_NAMESPACE, SET_ROUTING_ENABLED),
        [SET_ALLOWS_WET_SIGN]: getNamespacedResource(EDIT_ENVELOPE_NAMESPACE, SET_ALLOWS_WET_SIGN),
        [SET_ENVELOPE_TARGET_ATTACHMENT]: getNamespacedResource(
            EDIT_ENVELOPE_NAMESPACE,
            SET_ENVELOPE_TARGET_ATTACHMENT
        ),
        [SET_ENVELOPE_CERTIFICATE_OF_COMPLETION]: getNamespacedResource(
            EDIT_ENVELOPE_NAMESPACE,
            SET_ENVELOPE_CERTIFICATE_OF_COMPLETION
        ),
        [SET_ITEM_INCLUSION_FORMULA]: getNamespacedResource(EDIT_ENVELOPE_NAMESPACE, SET_ITEM_INCLUSION_FORMULA),
        [SET_ITEM_TARGET_ATTACHMENT]: getNamespacedResource(EDIT_ENVELOPE_NAMESPACE, SET_ITEM_TARGET_ATTACHMENT),
        [SET_ATTACHMENT_NAMES_INCLUDE_PROJECT_NUMBER]: getNamespacedResource(
            EDIT_ENVELOPE_NAMESPACE,
            SET_ATTACHMENT_NAMES_INCLUDE_PROJECT_NUMBER
        ),
        [SET_ALL_ITEM_ORDERS]: getNamespacedResource(EDIT_ENVELOPE_NAMESPACE, SET_ALL_ITEM_ORDERS),
        [SET_ITEM_IS_REQUIRED]: getNamespacedResource(EDIT_ENVELOPE_NAMESPACE, SET_ITEM_IS_REQUIRED),
        [SET_RECIPIENT_PRIVATE_MESSAGE]: getNamespacedResource(EDIT_ENVELOPE_NAMESPACE, SET_RECIPIENT_PRIVATE_MESSAGE),
        [SET_RECIPIENT_CONTACT_INFO_TYPE]: getNamespacedResource(
            EDIT_ENVELOPE_NAMESPACE,
            SET_RECIPIENT_CONTACT_INFO_TYPE
        ),
        [SET_MAPPING_DATA_TARGET]: getNamespacedResource(EDIT_ENVELOPE_NAMESPACE, SET_MAPPING_DATA_TARGET),
        [SET_ITEM_LABEL]: getNamespacedResource(EDIT_ENVELOPE_NAMESPACE, SET_ITEM_LABEL),
        [SET_SHOW_ENVELOPE_ERRORS]: getNamespacedResource(EDIT_ENVELOPE_NAMESPACE, SET_SHOW_ENVELOPE_ERRORS),
        [SET_MAPPING_FORMAT]: getNamespacedResource(EDIT_ENVELOPE_NAMESPACE, SET_MAPPING_FORMAT),
        [SET_MAPPING_IS_REQUIRED]: getNamespacedResource(EDIT_ENVELOPE_NAMESPACE, SET_MAPPING_IS_REQUIRED)
    },
    actions: {
        [OPEN_EDIT_ITEM_DIALOG]: getNamespacedResource(EDIT_ENVELOPE_NAMESPACE, OPEN_EDIT_ITEM_DIALOG),
        [OPEN_REMOVE_ITEM_DIALOG]: getNamespacedResource(EDIT_ENVELOPE_NAMESPACE, OPEN_REMOVE_ITEM_DIALOG),
        [ADD_ITEM]: getNamespacedResource(EDIT_ENVELOPE_NAMESPACE, ADD_ITEM),
        [REMOVE_ITEM]: getNamespacedResource(EDIT_ENVELOPE_NAMESPACE, REMOVE_ITEM),
        [SET_RECIPIENT_CONTACT_FIELD]: getNamespacedResource(EDIT_ENVELOPE_NAMESPACE, SET_RECIPIENT_CONTACT_FIELD),
        [SET_RECIPIENT_ROUTING_ORDER]: getNamespacedResource(EDIT_ENVELOPE_NAMESPACE, SET_RECIPIENT_ROUTING_ORDER),
        [SET_RECIPIENT_ROUTING_ORDER_MULTIPLE]: getNamespacedResource(
            EDIT_ENVELOPE_NAMESPACE,
            SET_RECIPIENT_ROUTING_ORDER_MULTIPLE
        ),
        [INITIALIZE_ALL_ITEM_ORDERS]: getNamespacedResource(EDIT_ENVELOPE_NAMESPACE, INITIALIZE_ALL_ITEM_ORDERS),
        [INITIALIZE_ITEM_MAPPINGS]: getNamespacedResource(EDIT_ENVELOPE_NAMESPACE, INITIALIZE_ITEM_MAPPINGS),
        [SANITIZE_ENVELOPE_FOR_SAVE]: getNamespacedResource(EDIT_ENVELOPE_NAMESPACE, SANITIZE_ENVELOPE_FOR_SAVE),
        [APPLY_MAPPING_DEFAULTS]: getNamespacedResource(EDIT_ENVELOPE_NAMESPACE, APPLY_MAPPING_DEFAULTS)
    }
};

const ENVELOPE_RECIPIENT_CONTACT_FIELD_TYPE = {
    FirstNameField: 1,
    LastNameField: 2,
    EmailField: 3,
    ContactGroup: 4,
    UserField: 5,
    FirstNameFixed: 6,
    LastNameFixed: 7,
    EmailFixed: 8
};

const ENVELOPE_RECIPIENT_CONTACT_INFO_TYPE = {
    IndividualFields: 1,
    UserField: 2,
    Fixed: 3
    // ContactGroup: 4 // TODO - would need a new DB column to differentiate from individual fields
};

const ENVELOPE_ITEM_TYPE = {
    Attachment: 1,
    ContentItem: 2,
    DocumentTemplate: 3
};

const ENVELOPE_RECIPIENT_TYPE = {
    Signatory: 1,
    Cc: 2
};

export {
    EDIT_ENVELOPE_NAMESPACE,
    EDIT_ENVELOPE_TYPES,
    ENVELOPE_ITEM_TYPE,
    ENVELOPE_RECIPIENT_TYPE,
    ENVELOPE_RECIPIENT_CONTACT_FIELD_TYPE,
    ENVELOPE_RECIPIENT_CONTACT_INFO_TYPE,
    getEditEnvelopeTooltipAlertBinder
};

/**
 * Create the Vuex module for editing envelope definitions.
 * State parameter should come from instance of ClientEnvelopeDefinitionState
 * ESignatureProviderCapabilities parameter should come from instance of ClientESignatureProviderCapabilities
 * @param {*} state
 * @param {*} ESignatureProviderCapabilities
 * @returns
 */
export function createEditEnvelopeModule(state, ESignatureProviderCapabilities, $app) {
    return {
        namespaced: true,
        state: {
            envelopeItemKeys: [],
            capabilities: ESignatureProviderCapabilities,
            ...state
        },
        getters: {
            [ITEM_LIST]: (state) => {
                return Object.values(state.EnvelopeDefinition.Items);
            },
            [ORDERED_ITEM_IDS]: (state, getters) => {
                const items = getters[ITEM_LIST];
                const ordered = [];
                // Build a proxy list that contains the item order and ID
                // so that we can sort it without having a side-effect on the
                // source list
                for (const item of items) {
                    ordered.push({ order: item.Order, id: item.PublicId });
                }
                ordered.sort((a, b) => numericCompare(a.order, b.order));
                return ordered.map((obj) => obj.id);
            },
            [PROVIDER_CAPABILITIES]: (state) => {
                return state.capabilities;
            },
            [GET_ITEM_BY_ID]: (state) =>
                function (id) {
                    return state.EnvelopeDefinition.Items[id];
                },
            [GET_ITEM_SOURCE_ID_BY_ITEM_ID]: (state, getters) =>
                function (itemId) {
                    const item = getters[GET_ITEM_BY_ID](itemId);
                    if (!item) {
                        return null;
                    }
                    switch (item.Type) {
                        case ENVELOPE_ITEM_TYPE.Attachment:
                            return item.AttachmentDefinitionId;
                        case ENVELOPE_ITEM_TYPE.ContentItem:
                            return item.ContentItemId;
                        case ENVELOPE_ITEM_TYPE.DocumentTemplate:
                            return item.DocumentTemplateDefinitionId;
                        default:
                            throw `Unhandled item type ${item.Type}`;
                    }
                },
            [GET_RECIPIENT_BY_LABEL]: (state, getters) =>
                function (label) {
                    const recipients = getters[RECIPIENT_LIST];
                    return recipients.find((r) => r.Label === label);
                },
            [RECIPIENT_LIST]: (state) => {
                return Object.values(state.EnvelopeDefinition.Recipients);
            },
            /**
             * @returns Array of recipient IDs ordered alphabetically by recipient Label
             */
            [ORDERED_RECIPIENT_IDS]: (state, getters) => {
                const unsortedList = getters[RECIPIENT_LIST];
                const ordered = unsortedList.map((recipient) => {
                    return {
                        label: recipient.Label,
                        id: recipient.PublicId
                    };
                });
                ordered.sort((a, b) => a.label.localeCompare(b.label));
                return ordered.map((obj) => obj.id);
            },
            [RECIPIENT_LABEL_SET]: (state, getters) => {
                const labelList = getters[RECIPIENT_LIST].map((recipient) => recipient.Label);
                return new Set(labelList);
            },
            [GET_RECIPIENT_BY_ID]: (state) =>
                function (id) {
                    return state.EnvelopeDefinition.Recipients[id];
                },
            [GET_AVAILABLE_ATTACHMENT_DEFINITIONS]: (state, getters, rootState, rootGetters) =>
                function (selectedId) {
                    const attachmentDefinitions = rootGetters[PROGRAM_TYPES.getters.getAttachmentList]();
                    const inUseAttachmentDefinitionIds = getters.inUseAttachmentDefinitionIds;
                    return attachmentDefinitions.filter(
                        (a) =>
                            a.PublicId === selectedId || // Allow including a currently selected attachment
                            !inUseAttachmentDefinitionIds.has(a.PublicId)
                    );
                },
            [ITEMS_BY_RECIPIENT_ID]: (state, getters, rootState, rootGetters) => {
                const recipients = getters[RECIPIENT_LIST];
                const items = getters[ITEM_LIST];
                const docTemplateItems = items.filter((item) => item.Type === ENVELOPE_ITEM_TYPE.DocumentTemplate);

                // First build dictionary mapping all signatory labels on all items to the
                // items on which they appear
                const itemsByLabel = {};
                for (const item of docTemplateItems) {
                    const signatoryLabelSet = rootGetters[
                        DOCUMENT_TEMPLATE_TYPES.getters.getCurrentSignatoryLabelSetFromDefinitionId
                    ](item.DocumentTemplateDefinitionId);
                    for (const label of signatoryLabelSet) {
                        let itemListForLabel;
                        if (itemsByLabel.hasOwnProperty(label)) {
                            itemListForLabel = itemsByLabel[label];
                        } else {
                            itemListForLabel = [];
                            itemsByLabel[label] = itemListForLabel;
                        }
                        itemListForLabel.push(item);
                    }
                }

                // Translate that map to a map from recipient ID to the item list using
                // recipient label
                const itemsByRecipientId = {};
                for (const recipient of recipients) {
                    const itemList = itemsByLabel[recipient.Label];
                    itemsByRecipientId[recipient.PublicId] = itemList;
                }
                return itemsByRecipientId;
            },
            /**
             * Given an Item ID, get a list of recipients that exist only on that item and
             * not on any other items
             */
            [GET_RECIPIENTS_EXCLUSIVE_TO_ITEM]: (state, getters) =>
                function (itemId) {
                    const item = getters[GET_ITEM_BY_ID](itemId);
                    if (item.Type !== ENVELOPE_ITEM_TYPE.DocumentTemplate) {
                        return [];
                    }

                    const itemRecipients = getters.getRecipientsByItemId(itemId);
                    const itemsByRecipientId = getters[ITEMS_BY_RECIPIENT_ID];
                    const exclusiveRecipients = [];

                    // Look through all recipients associated with this item
                    for (const recipient of itemRecipients) {
                        const recipientItems = itemsByRecipientId[recipient.PublicId];
                        // If the recipient is only associated with one item and it's this one,
                        // mark them to be removed
                        if (recipientItems.length === 1 && recipientItems[0].PublicId === itemId) {
                            exclusiveRecipients.push(recipient);
                        }
                    }
                    return exclusiveRecipients;
                },
            [MAPPING_LIST]: (state) => {
                return Object.values(state.EnvelopeDefinition.Mappings);
            },
            [GET_MAPPING_BY_ID]: (state) =>
                function (mappingId) {
                    return state.EnvelopeDefinition.Mappings[mappingId];
                },
            [GET_MAPPINGS_BY_ITEM_ID]: (state, getters) =>
                function (itemId) {
                    const mappings = getters[MAPPING_LIST];
                    return mappings.filter((efm) => efm.ItemId === itemId);
                },
            [GET_MAPPING_TARGET_OPTIONS_BY_MAPPING_ID]: (state, getters, rootState, rootGetters) =>
                function (mappingId) {
                    const mapping = getters[GET_MAPPING_BY_ID](mappingId);
                    const itemMappings = getters[GET_MAPPINGS_BY_ITEM_ID](mapping.ItemId);
                    const currentTarget = mapping.DataTarget;

                    // Build a set of target IDs that have been used by mappings in the
                    // same envelope item. This is to avoid the situation where multiple mappings
                    // in the same item point to the same target, since that's probably a
                    // misconfiguration.
                    const usedTargets = new Set();
                    itemMappings
                        .filter((efm) => efm.DataTarget != null)
                        .forEach((efm) => {
                            usedTargets.add(efm.DataTarget.PublicId);
                        });

                    function getFieldsByType(typeId) {
                        return rootGetters[PROGRAM_TYPES.getters.getSingleInstanceDataFieldListByDataFieldType](typeId);
                    }
                    function getGroupsByType(typeId) {
                        return rootGetters[PROGRAM_TYPES.getters.dataFieldGroupDefinitionsFromType](typeId);
                    }

                    const stringFieldTargets = getFieldsByType(DATA_FIELD_TYPE.String).map((field) => {
                        return {
                            text: field.Name,
                            value: field.PublicId,
                            type: SIGNATORY_DATA_TARGET_TYPE.StringField
                        };
                    });
                    const dateFieldTargets = getFieldsByType(DATA_FIELD_TYPE.DateTime).map((field) => {
                        return {
                            text: field.Name,
                            value: field.PublicId,
                            type: SIGNATORY_DATA_TARGET_TYPE.DateField
                        };
                    });
                    const sensitiveGroupTargets = getGroupsByType(DATA_FIELD_GROUP_TYPE.Sensitive).map((group) => {
                        return {
                            text: group.Name,
                            value: group.PublicId,
                            type: SIGNATORY_DATA_TARGET_TYPE.SensitiveGroup
                        };
                    });

                    let targetFieldOptions = [];
                    let targetGroupOptions = [];
                    switch (mapping.TagType) {
                        case TEMPLATE_TAG_TYPE_CONSTANT.SignatoryTextInput:
                            targetFieldOptions = stringFieldTargets;
                            targetGroupOptions = sensitiveGroupTargets;
                            break;
                        case TEMPLATE_TAG_TYPE_CONSTANT.SignatoryDateSigned:
                            targetFieldOptions = dateFieldTargets;
                            break;
                        case TEMPLATE_TAG_TYPE_CONSTANT.SignatorySensitiveInput:
                            targetGroupOptions = sensitiveGroupTargets;
                            break;
                        default:
                            return [];
                    }

                    // Options for this mapping consist of any fields/groups that are valid
                    // for this tag type and are not the target of another mapping in this item
                    const allOptions = targetFieldOptions.concat(targetGroupOptions).filter((option) => {
                        return (
                            (currentTarget && currentTarget.PublicId == option.value) || !usedTargets.has(option.value)
                        );
                    });
                    return allOptions.sort((a, b) => a.text.localeCompare(b.text));
                },
            itemCount: (state, getters) => {
                return getters[ITEM_LIST].length;
            },
            inUseAttachmentDefinitionIds: (state) => {
                const inUseIds = new Set();

                // In-use attachment definitions include those used as targets for
                // individual envelope item documents or the combined envelope document,
                // as well as any source attachments for items
                if (state.EnvelopeDefinition.TargetAttachmentDefinitionId) {
                    inUseIds.add(state.EnvelopeDefinition.TargetAttachmentDefinitionId);
                }
                if (state.EnvelopeDefinition.CertificateTargetAttachmentDefinitionId) {
                    inUseIds.add(state.EnvelopeDefinition.CertificateTargetAttachmentDefinitionId);
                }

                for (const itemId in state.EnvelopeDefinition.Items) {
                    if (state.EnvelopeDefinition.Items.hasOwnProperty(itemId)) {
                        const item = state.EnvelopeDefinition.Items[itemId];
                        if (item.TargetAttachmentDefinitionId) {
                            inUseIds.add(item.TargetAttachmentDefinitionId);
                        }
                        if (item.AttachmentDefinitionId) {
                            inUseIds.add(item.AttachmentDefinitionId);
                        }
                    }
                }

                return inUseIds;
            },
            [GET_RECIPIENTS_BY_ITEM_ID]: (state, getters, rootState, rootGetters) =>
                function (itemId) {
                    const item = getters[GET_ITEM_BY_ID](itemId);
                    if (item.Type !== ENVELOPE_ITEM_TYPE.DocumentTemplate) {
                        return [];
                    }

                    const labelSet = rootGetters[
                        DOCUMENT_TEMPLATE_TYPES.getters.getCurrentSignatoryLabelSetFromDefinitionId
                    ](item.DocumentTemplateDefinitionId);
                    const labels = [...labelSet];
                    return labels.map((label) => getters[GET_RECIPIENT_BY_LABEL](label));
                },
            [GET_UNAPPLIED_MAPPINGS_BY_ITEM_ID]: (state, getters, rootState, rootGetters) =>
                function (itemId, templateDefinitionId) {
                    const allTemplateTags =
                        rootGetters[DOCUMENT_TEMPLATE_TYPES.getters.getCurrentMappableSignatoryTagsFromDefinitionId](
                            templateDefinitionId
                        ).allTags;
                    for (const mapping of getters[GET_MAPPINGS_BY_ITEM_ID](itemId).filter(
                        (m) => !m.MergeLinkTemplateId || m.MergeLinkTemplateId == templateDefinitionId
                    )) {
                        const recipient = getters[GET_RECIPIENT_BY_ID](mapping.RecipientId);
                        const templateTag = allTemplateTags.find(
                            (tt) =>
                                tt.SignatoryTagNumber == mapping.SignatoryTagNumber &&
                                tt.TagType == mapping.TagType &&
                                tt.SignatoryLabel == recipient.Label
                        );
                        if (
                            templateTag?.SignatoryTargetObject?.PublicId &&
                            (mapping.DataTarget?.PublicId != templateTag?.SignatoryTargetObject?.PublicId ||
                                mapping.Properties?.Format != templateTag?.TagProperties?.Format)
                        ) {
                            return true;
                        }
                    }
                    return false;
                },
            recipientCount: (state, getters) => {
                return getters[RECIPIENT_LIST].length;
            },
            [SHOW_ENVELOPE_ERRORS]: (state) => {
                return state.ShowEnvelopeErrors;
            },
            [ENVELOPE_VALIDATION]: (state, getters) => {
                const errors = [];
                if (!state.EnvelopeDefinition.Name) {
                    errors.push(
                        $app.res({
                            namespace: NAMESPACE,
                            key: KEYS.envelopeNameError
                        })
                    );
                }
                if (!state.EnvelopeDefinition.EmailSubject) {
                    errors.push(
                        $app.res({
                            namespace: NAMESPACE,
                            key: KEYS.emailSubjectError
                        })
                    );
                }
                if (!state.EnvelopeDefinition.TargetAttachmentDefinitionId) {
                    errors.push(
                        $app.res({
                            namespace: NAMESPACE,
                            key: KEYS.attachmentDefinitionError
                        })
                    );
                }
                const recipientIds = getters[ORDERED_RECIPIENT_IDS];
                if (!(recipientIds?.length > 0)) {
                    errors.push(
                        $app.res({
                            namespace: NAMESPACE,
                            key: KEYS.validsignatoryError
                        })
                    );
                }
                for (const recipientId of recipientIds) {
                    if (!getters[GET_ENVELOPE_RECIPIENT_VALID](recipientId)) {
                        errors.push(
                            $app.res({
                                namespace: NAMESPACE,
                                key: KEYS.recipentFieldError
                            })
                        );
                    }
                    const recipientContactInfoValid =
                        getters[IS_INDIVIDUAL_FIELDS_RECIPIENT_CONTACT_INFO_VALID](recipientId);
                    if (recipientContactInfoValid.error != null) {
                        errors.push(recipientContactInfoValid.error);
                    }
                    const fixedRecipientNameValid = getters[IS_FIXED_RECIPIENT_NAME_VALID](recipientId);
                    if (fixedRecipientNameValid.error != null) {
                        errors.push(fixedRecipientNameValid.error);
                    }
                    const fixedRecipientEmailLengthValid = getters[IS_FIXED_RECIPIENT_EMAIL_LENGTH_VALID](recipientId);
                    if (fixedRecipientEmailLengthValid.error != null) {
                        errors.push(fixedRecipientEmailLengthValid.error);
                    }
                    const fixedRecipientEmailFormatValid = getters[IS_FIXED_RECIPIENT_EMAIL_FORMAT_VALID](recipientId);
                    if (fixedRecipientEmailFormatValid.error != null) {
                        errors.push(fixedRecipientEmailFormatValid.error);
                    }
                }
                return errors;
            },
            [IS_FIXED_RECIPIENT_NAME_VALID]: (state, getters) =>
                function (recipientId) {
                    const recipient = getters[GET_RECIPIENT_BY_ID](recipientId);
                    const valid = {
                        isValid: false,
                        error: null
                    };
                    //This error will be handled by the required validator, must return true here in order for
                    //"This field is required" text to appear below the correct contact info field
                    //In JavaScript, an empty string is false
                    if (!recipient.FirstNameFixed || !recipient.LastNameFixed) {
                        valid.isValid = true;
                        return valid;
                    }
                    const fullName = recipient.FirstNameFixed + " " + recipient.LastNameFixed;
                    if (fullName.length > state.capabilities.RecipientNameLengthLimit) {
                        valid.error = $app.res({
                            namespace: NAMESPACE,
                            key: KEYS.fixedFullNameMaxLengthValidationMessage,
                            values: { maxLength: state.capabilities.EmailAddressLengthLimit }
                        });
                    } else {
                        valid.isValid = true;
                    }
                    return valid;
                },
            [IS_FIXED_RECIPIENT_EMAIL_LENGTH_VALID]: (state, getters) =>
                function (recipientId) {
                    const recipient = getters[GET_RECIPIENT_BY_ID](recipientId);
                    const valid = {
                        isValid: false,
                        error: null
                    };
                    //This error will be handled by the required validator, must return true here in order for
                    //"This field is required" text to appear below the correct contact info field
                    //In JavaScript, an empty string is false
                    if (!recipient.EmailFixed) {
                        valid.isValid = true;
                        return valid;
                    }
                    if (recipient.EmailFixed.length > state.capabilities.EmailAddressLengthLimit) {
                        valid.error = $app.res({
                            namespace: NAMESPACE,
                            key: KEYS.fixedEmailMaxLengthValidationMessage,
                            values: { maxLength: state.capabilities.EmailAddressLengthLimit }
                        });
                    } else {
                        valid.isValid = true;
                    }
                    return valid;
                },
            // This method is effectively a normal email validator but
            // the error text will be passed correctly into ENVELOPE_VALIDATION.
            // This error text is displayed at the bottom of the edit Envelope page,
            // preventing a user from saving until the error has been addressed.
            [IS_FIXED_RECIPIENT_EMAIL_FORMAT_VALID]: (state, getters) =>
                function (recipientId) {
                    const recipient = getters[GET_RECIPIENT_BY_ID](recipientId);
                    const valid = {
                        isValid: false,
                        error: null
                    };
                    //This error will be handled by the required validator, must return true here in order for
                    //"This field is required" text to appear below the correct contact info field
                    //In JavaScript, an empty string is false
                    if (!recipient.EmailFixed) {
                        valid.isValid = true;
                        return valid;
                    }
                    const emailValidator = getValidator($app, VALIDATOR_TYPES.email);
                    const result = emailValidator.validate(recipient.EmailFixed);
                    if (result.hasError) {
                        valid.error = result.error;
                    } else {
                        valid.isValid = true;
                    }
                    return valid;
                },
            [GET_ENVELOPE_RECIPIENT_VALID]: (state, getters) =>
                function (recipientId) {
                    const recipient = getters[GET_RECIPIENT_BY_ID](recipientId);
                    let isValid = true;
                    if (!recipient) {
                        return false;
                    }
                    if (recipient.ContactInfoType == ENVELOPE_RECIPIENT_CONTACT_INFO_TYPE.IndividualFields) {
                        isValid = isValid && Boolean(recipient.FirstNameFieldId);
                        isValid = isValid && Boolean(recipient.LastNameFieldId);
                        isValid = isValid && Boolean(recipient.EmailFieldId);
                    } else if (recipient.ContactInfoType == ENVELOPE_RECIPIENT_CONTACT_INFO_TYPE.UserField) {
                        isValid = isValid && Boolean(recipient.UserFieldId);
                    } else if (recipient.ContactInfoType == ENVELOPE_RECIPIENT_CONTACT_INFO_TYPE.Fixed) {
                        isValid = isValid && Boolean(recipient.FirstNameFixed);
                        isValid = isValid && Boolean(recipient.LastNameFixed);
                        isValid = isValid && Boolean(recipient.EmailFixed);
                    } else {
                        throw `not implemented for ${recipient.ContactInfoType}`;
                    }
                    return isValid;
                },
            //returns an object containing a boolean and a key string. The boolean is used in envelopeRecipientDefinition.vue to
            //display an error on the selector options, the key is used in this file to pass error text into ENVELOPE_VALIDATION
            [IS_INDIVIDUAL_FIELDS_RECIPIENT_CONTACT_INFO_VALID]: (state, getters, rootState, rootGetters) =>
                function (recipientId) {
                    const valid = {
                        isValid: false,
                        error: null
                    };
                    const recipient = getters[GET_RECIPIENT_BY_ID](recipientId);
                    const firstField = rootGetters[PROGRAM_TYPES.getters.dataFieldDefinitionFromId](
                        recipient.FirstNameFieldId
                    );
                    const lastField = rootGetters[PROGRAM_TYPES.getters.dataFieldDefinitionFromId](
                        recipient.LastNameFieldId
                    );
                    const emailField = rootGetters[PROGRAM_TYPES.getters.dataFieldDefinitionFromId](
                        recipient.EmailFieldId
                    );

                    //This error will be handled by the required validator, must return true here in order for
                    //"This field is required" text to appear below the correct contact info field
                    if (firstField == null || lastField == null || emailField == null) {
                        valid.isValid = true;
                        return valid;
                    }

                    const firstFieldGroupType = rootGetters[PROGRAM_TYPES.getters.dataFieldGroupDefinitionFromId](
                        firstField.DataFieldGroupId
                    ).TypeId;
                    let areNamesInContactGroup = false;

                    //checks if Data Field Group Id is equal between first and last name
                    if (firstField.DataFieldGroupId === lastField.DataFieldGroupId) {
                        if (firstFieldGroupType === DATA_FIELD_GROUP_TYPE.Contact) {
                            areNamesInContactGroup = true;
                        } else if (firstFieldGroupType !== DATA_FIELD_GROUP_TYPE.Name) {
                            //throw an error, first and last are not from a name or contact group
                            valid.error = $app.res({
                                namespace: NAMESPACE,
                                key: KEYS.firstAndLastNameNotInContactOrNameGroupErrorMessage
                            });
                            return valid;
                        }
                    } else {
                        //throw an error, first and last name are not from the same group
                        valid.error = $app.res({
                            namespace: NAMESPACE,
                            key: KEYS.firstAndLastNameNotInSameGroupErrorMessage
                        });
                        return valid;
                    }

                    const emailFieldGroupDefinition = rootGetters[PROGRAM_TYPES.getters.dataFieldGroupDefinitionFromId](
                        emailField.DataFieldGroupId
                    );
                    if (areNamesInContactGroup) {
                        //Checks to see if Data Field Group Id for emailField is the same as first and last name
                        if (emailField.DataFieldGroupId === firstField.DataFieldGroupId) {
                            valid.isValid = true;
                        } else {
                            //throw an error, If all 3 are specified and first and last are from a contact
                            //group, email must be from the same contact group
                            valid.error = $app.res({
                                namespace: NAMESPACE,
                                key: KEYS.emailNotInSameContactGroupAsFirstAndLastNameErrorMessage
                            });
                        }
                    } else {
                        if (!emailFieldGroupDefinition) {
                            valid.isValid = true;
                        } else if (emailFieldGroupDefinition.TypeId === DATA_FIELD_GROUP_TYPE.Contact) {
                            //throw an error, email field belongs to same contact group as first and last
                            //name, or to no group
                            valid.error = valid.error = $app.res({
                                namespace: NAMESPACE,
                                key: KEYS.emailNotInSameContactGroupAsFirstAndLastNameErrorMessage
                            });
                        }
                    }
                    return valid;
                }
        },
        mutations: {
            [SET_ENVELOPE_NAME](state, { name }) {
                state.EnvelopeDefinition.Name = name;
            },
            [SET_ENVELOPE_DESCRIPTION](state, { description }) {
                state.EnvelopeDefinition.Description = description;
            },
            [SET_ENVELOPE_EMAIL_SUBJECT](state, { emailSubject }) {
                state.EnvelopeDefinition.EmailSubject = emailSubject;
            },
            [SET_ENVELOPE_EMAIL_MESSAGE](state, { emailMessage }) {
                state.EnvelopeDefinition.EmailMessage = emailMessage;
            },
            [SET_ROUTING_ENABLED](state, { enabled }) {
                state.EnvelopeDefinition.RoutingEnabled = enabled;

                // Need to set up or clear routing order of all recipients when this changes
                const order = 1;
                for (const recipientId in state.EnvelopeDefinition.Recipients) {
                    if (state.EnvelopeDefinition.Recipients.hasOwnProperty(recipientId)) {
                        const recipient = state.EnvelopeDefinition.Recipients[recipientId];
                        if (enabled && !recipient.RoutingOrder) {
                            recipient.RoutingOrder = order;
                        }
                    }
                }
            },
            [SET_ALLOWS_WET_SIGN](state, { enabled }) {
                state.EnvelopeDefinition.AllowsWetSign = enabled;
            },
            [SET_ENVELOPE_TARGET_ATTACHMENT](state, { attachmentId }) {
                state.EnvelopeDefinition.TargetAttachmentDefinitionId = attachmentId;
            },
            [SET_ENVELOPE_CERTIFICATE_OF_COMPLETION](state, { attachmentId }) {
                state.EnvelopeDefinition.CertificateTargetAttachmentDefinitionId = attachmentId;
            },
            [SET_ITEM_INCLUSION_FORMULA](state, { itemId, formulaId }) {
                const item = state.EnvelopeDefinition.Items[itemId];
                item.IsIncludedFormulaId = formulaId;
            },
            [SET_ITEM_TARGET_ATTACHMENT](state, { itemId, attachmentId }) {
                const item = state.EnvelopeDefinition.Items[itemId];
                item.TargetAttachmentDefinitionId = attachmentId;
            },
            [SET_ATTACHMENT_NAMES_INCLUDE_PROJECT_NUMBER](state, { enabled }) {
                state.EnvelopeDefinition.AttachmentNamesIncludeProjectNumber = enabled;
            },
            [SET_ALL_ITEM_ORDERS](state, { orderedItemIds }) {
                state.envelopeItemKeys = orderedItemIds;
                let order = 1;
                for (const itemId of orderedItemIds) {
                    const item = state.EnvelopeDefinition.Items[itemId];
                    item.Order = order;
                    order++;
                }
            },
            [SET_ITEM_IS_REQUIRED](state, { itemId, isRequired }) {
                const item = state.EnvelopeDefinition.Items[itemId];
                item.IsRequired = isRequired;
            },
            [SET_ITEM_LABEL](state, { itemId, label }) {
                const item = state.EnvelopeDefinition.Items[itemId];
                item.Label = label;
            },
            [SET_SHOW_ENVELOPE_ERRORS](state, { showEnvelopeErrors }) {
                state.ShowEnvelopeErrors = showEnvelopeErrors;
            },
            [SET_RECIPIENT_PRIVATE_MESSAGE](state, { recipientId, message }) {
                const recipient = state.EnvelopeDefinition.Recipients[recipientId];
                recipient.Message = message;
            },
            [SET_RECIPIENT_CONTACT_INFO_TYPE](state, { recipientId, contactInfoType }) {
                const recipient = state.EnvelopeDefinition.Recipients[recipientId];
                recipient.ContactInfoType = contactInfoType;
            },
            [SET_MAPPING_DATA_TARGET](state, { mappingId, targetId, targetType }) {
                const mapping = state.EnvelopeDefinition.Mappings[mappingId];
                let dataTarget = null;
                if (targetId) {
                    dataTarget = {
                        PublicId: targetId,
                        TargetType: targetType
                    };
                }
                mapping.DataTarget = dataTarget;
            },
            [SET_MAPPING_FORMAT](state, { mappingId, format }) {
                const mapping = state.EnvelopeDefinition.Mappings[mappingId];
                if (!mapping.Properties) {
                    mapping.Properties = {};
                }
                mapping.Properties.Format = format;
            },
            [SET_MAPPING_IS_REQUIRED](state, { mappingId, isRequired }) {
                const mapping = state.EnvelopeDefinition.Mappings[mappingId];
                mapping.Properties.IsRequired = isRequired;
            },
            // Private Mutations
            [_INSERT_NEW_ITEM](state, { newItem }) {
                state.EnvelopeDefinition.Items[newItem.PublicId] = newItem;
                state.envelopeItemKeys.push(newItem.PublicId);
            },
            [_REMOVE_ITEM](state, { itemId }) {
                delete state.EnvelopeDefinition.Items[itemId];
                state.envelopeItemKeys = state.envelopeItemKeys.filter((id) => id !== itemId);
            },
            [_INSERT_NEW_RECIPIENT](state, { newRecipient }) {
                state.EnvelopeDefinition.Recipients[newRecipient.PublicId] = newRecipient;
            },
            [_COMMIT_REMOVE_RECIPIENT](state, { recipientId }) {
                delete state.EnvelopeDefinition.Recipients[recipientId];
            },
            [_COMMIT_RECIPIENT_CONTACT_INFO_SOURCE](state, { recipientId, fieldType, fieldId, fixedValue }) {
                const recipient = state.EnvelopeDefinition.Recipients[recipientId];
                switch (fieldType) {
                    case ENVELOPE_RECIPIENT_CONTACT_FIELD_TYPE.FirstNameField:
                        recipient.FirstNameFieldId = fieldId;
                        break;
                    case ENVELOPE_RECIPIENT_CONTACT_FIELD_TYPE.LastNameField:
                        recipient.LastNameFieldId = fieldId;
                        break;
                    case ENVELOPE_RECIPIENT_CONTACT_FIELD_TYPE.EmailField:
                        recipient.EmailFieldId = fieldId;
                        break;
                    case ENVELOPE_RECIPIENT_CONTACT_FIELD_TYPE.UserField:
                        recipient.UserFieldId = fieldId;
                        break;
                    case ENVELOPE_RECIPIENT_CONTACT_FIELD_TYPE.FirstNameFixed:
                        recipient.FirstNameFixed = fixedValue;
                        break;
                    case ENVELOPE_RECIPIENT_CONTACT_FIELD_TYPE.LastNameFixed:
                        recipient.LastNameFixed = fixedValue;
                        break;
                    case ENVELOPE_RECIPIENT_CONTACT_FIELD_TYPE.EmailFixed:
                        recipient.EmailFixed = fixedValue;
                        break;
                    default:
                        throw `Unhandled fieldType ${fieldType}`;
                }
            },
            [_SET_SINGLE_RECIPIENT_ROUTING_ORDER](state, { recipientId, routingOrder }) {
                const recipient = state.EnvelopeDefinition.Recipients[recipientId];
                recipient.RoutingOrder = routingOrder;
            },
            [_SET_MULTIPLE_RECIPIENT_ROUTING_ORDERS](state, orders) {
                for (const order of orders) {
                    state.EnvelopeDefinition.Recipients[order.recipientId].RoutingOrder = order.routingOrder;
                }
            },
            [_INSERT_NEW_MAPPING](state, { newMapping }) {
                state.EnvelopeDefinition.Mappings[newMapping.PublicId] = newMapping;
            },
            [_REMOVE_MAPPING](state, { mappingId }) {
                delete state.EnvelopeDefinition.Mappings[mappingId];
            },
            [_SET_ITEM_ATTACHMENT_DEFINITION_ID](state, { itemId, newAttachmentDefId }) {
                const item = state.EnvelopeDefinition.Items[itemId];
                if (item.Type !== ENVELOPE_ITEM_TYPE.Attachment) {
                    throw `Tried to set attachment definition ID for non-attachment item <${item}>`;
                }
                item.AttachmentDefinitionId = newAttachmentDefId;
            },
            [_SET_ITEM_CONTENT_ITEM_ID](state, { itemId, newContentItemId }) {
                const item = state.EnvelopeDefinition.Items[itemId];
                if (item.Type !== ENVELOPE_ITEM_TYPE.ContentItem) {
                    throw `Tried to set content ID for non-content library item <${item}>`;
                }
                item.ContentItemId = newContentItemId;
            },
            [_SET_ITEM_DOCUMENT_TEMPLATE_DEFINITION_ID](state, { itemId, newDocTemplateId }) {
                const item = state.EnvelopeDefinition.Items[itemId];
                if (item.Type !== ENVELOPE_ITEM_TYPE.DocumentTemplate) {
                    throw `Tried to set document template ID for non-document template item <${item}>`;
                }
                item.DocumentTemplateDefinitionId = newDocTemplateId;
            }
        },
        actions: {
            [INITIALIZE_ALL_ITEM_ORDERS]({ getters, commit }) {
                const orderedItemIds = getters[ORDERED_ITEM_IDS];
                const orderedItemIdsCopy = orderedItemIds.slice();
                commit(SET_ALL_ITEM_ORDERS, { orderedItemIds: orderedItemIdsCopy });
            },
            async [OPEN_EDIT_ITEM_DIALOG](
                { getters, rootGetters, commit, dispatch },
                { dialogComponent, initialType, initialItemId }
            ) {
                let initialItem;
                const isEditExisting = Boolean(initialItemId);
                if (isEditExisting) {
                    const item = getters[GET_ITEM_BY_ID](initialItemId);
                    const sourceId = getters[GET_ITEM_SOURCE_ID_BY_ITEM_ID](initialItemId);
                    initialItem = {
                        type: item.Type,
                        sourceId,
                        isIncludedFormulaId: item.IsIncludedFormulaId,
                        targetAttachmentDefinitionId: item.TargetAttachmentDefinitionId,
                        isRequired: item.IsRequired,
                        label: item.Label
                    };
                }
                const editTitle = $app.res({
                    namespace: NAMESPACE,
                    key: KEYS.editItemTitle
                });
                const addTitle = $app.res({
                    namespace: NAMESPACE,
                    key: KEYS.addItemTitle
                });
                const title = isEditExisting ? editTitle : addTitle;
                const { action, data } = await $app.showDialog({
                    title: title,
                    customComponent: dialogComponent,
                    customComponentOptions: {
                        initialType,
                        initialItem
                    },
                    customComponentResultHook: DIALOG_HOOK.getResult,
                    customComponentIsValidToCloseHookAsync: DIALOG_HOOK.isValidToClose,
                    sidePanel: true,
                    buttons: [
                        {
                            text: $app.res({
                                namespace: COMMON_NAMESPACE,
                                key: COMMON_KEYS.cancelText
                            }),
                            result: DIALOG_RESULT.cancel
                        },
                        {
                            text: $app.res({
                                namespace: COMMON_NAMESPACE,
                                key: COMMON_KEYS.okText
                            }),
                            primary: true,
                            result: DIALOG_RESULT.ok
                        }
                    ]
                });

                if (action == DIALOG_RESULT.cancel) {
                    return;
                }

                let itemId = initialItemId;

                // if the source ID was changed
                // if (initialItemId !== data.sourceId)

                if (!isEditExisting) {
                    // New Item
                    itemId = await dispatch(ADD_ITEM, { type: data.type, sourceId: data.sourceId });
                } else if (initialItemId !== data.sourceId) {
                    switch (initialItem.type) {
                        case ENVELOPE_ITEM_TYPE.Attachment:
                            commit(_SET_ITEM_ATTACHMENT_DEFINITION_ID, {
                                itemId: itemId,
                                newAttachmentDefId: data.sourceId
                            });
                            break;
                        case ENVELOPE_ITEM_TYPE.ContentItem:
                            commit(_SET_ITEM_CONTENT_ITEM_ID, { itemId: itemId, newContentItemId: data.sourceId });
                            break;
                        case ENVELOPE_ITEM_TYPE.DocumentTemplate:
                            changeDocumentTemplateItemSource(
                                initialItemId,
                                data.sourceId,
                                state,
                                getters,
                                rootGetters,
                                commit,
                                dispatch
                            );
                            break;
                        default:
                            throw `Unhandled item type <${initialItem.type}>`;
                    }
                }
                commit(SET_ITEM_TARGET_ATTACHMENT, {
                    itemId: itemId,
                    attachmentId: data.targetAttachmentDefinitionId
                });
                commit(SET_ITEM_INCLUSION_FORMULA, {
                    itemId: itemId,
                    formulaId: data.isIncludedFormulaId
                });
                commit(SET_ITEM_IS_REQUIRED, {
                    itemId: itemId,
                    isRequired: data.isRequired
                });
                commit(SET_ITEM_LABEL, {
                    itemId: itemId,
                    label: data.label
                });
                return itemId;
            },
            async [OPEN_REMOVE_ITEM_DIALOG]({ getters, dispatch }, itemId) {
                const recipientsToRemove = getters[GET_RECIPIENTS_EXCLUSIVE_TO_ITEM](itemId);
                const item = getters[GET_ITEM_BY_ID](itemId);
                const titleKey = item.Label ?? item.PublicId;
                const messageTitle = $app.res({
                    namespace: NAMESPACE,
                    key: KEYS.removeMessageTitle,
                    values: { itemRemoved: titleKey }
                });
                let messageStr = $app.res({
                    namespace: NAMESPACE,
                    key: KEYS.removeItemConfirmation
                });
                if (recipientsToRemove.length > 0) {
                    const recipientsStr = recipientsToRemove.map((r) => r.Label).join(", ");
                    messageStr += $app.res({
                        namespace: NAMESPACE,
                        key: KEYS.removeMessageString,
                        values: { recipientsRemoved: recipientsStr }
                    });
                }

                if (!(await $app.showConfirmationDialog(messageTitle, messageStr))) {
                    return;
                }

                await dispatch(REMOVE_ITEM, { itemId });
                return itemId;
            },
            [ADD_ITEM]({ state, getters, commit, rootGetters, dispatch }, { type, sourceId }) {
                const index = getters.itemCount;
                const item = getNewEnvelopeItem(index, type, sourceId);

                commit(_INSERT_NEW_ITEM, { newItem: item });

                // Add new recipients if necessary based on signatory tags
                // present in item
                if (item.Type === ENVELOPE_ITEM_TYPE.DocumentTemplate) {
                    initializeDocumentTemplateItem(item, state, rootGetters, getters, commit, dispatch);
                }
                return item.PublicId;
            },
            async [REMOVE_ITEM]({ state, getters, commit }, { itemId }) {
                const item = getters[GET_ITEM_BY_ID](itemId);

                if (item.Type === ENVELOPE_ITEM_TYPE.DocumentTemplate) {
                    // Remove all mappings that belong to this item
                    const mappings = getters[GET_MAPPINGS_BY_ITEM_ID](itemId);
                    for (const mapping of mappings) {
                        commit(_REMOVE_MAPPING, { mappingId: mapping.PublicId });
                    }

                    // Remove any recipients that are associated exclusively with this item
                    const recipientsToRemove = getters[GET_RECIPIENTS_EXCLUSIVE_TO_ITEM](itemId);
                    for (const recipient of recipientsToRemove) {
                        removeRecipient({ state, getters, commit }, { recipientId: recipient.PublicId });
                    }
                }

                commit(_REMOVE_ITEM, { itemId });

                const orderedItemIds = getters[ORDERED_ITEM_IDS];
                commit(SET_ALL_ITEM_ORDERS, { orderedItemIds: orderedItemIds });
            },
            [INITIALIZE_ITEM_MAPPINGS]({ commit, getters, rootGetters }, { itemId }) {
                const item = getters[GET_ITEM_BY_ID](itemId);

                if (item.Type !== ENVELOPE_ITEM_TYPE.DocumentTemplate) {
                    return;
                }
                const templateDefinitionId = item.DocumentTemplateDefinitionId;
                const existingMappings = getters[GET_MAPPINGS_BY_ITEM_ID](itemId);

                function addMappingForTagIfNecessary(tag, mergeLinkTemplateId) {
                    const recipient = getters[GET_RECIPIENT_BY_LABEL](tag.SignatoryLabel);
                    const mapping = getNewEnvelopeFieldMapping(
                        item.PublicId,
                        recipient.PublicId,
                        tag,
                        mergeLinkTemplateId
                    );

                    if (!hasExistingMapping(existingMappings, mapping)) {
                        commit(_INSERT_NEW_MAPPING, { newMapping: mapping });
                    }
                }

                // Create new mapping objects as necessary
                const mappableTags =
                    rootGetters[DOCUMENT_TEMPLATE_TYPES.getters.getCurrentMappableSignatoryTagsFromDefinitionId](
                        templateDefinitionId
                    );

                if (Object.entries(mappableTags.tagsByLinkTemplateDefinitionId).length == 0) {
                    for (const tag of mappableTags.allTags) {
                        addMappingForTagIfNecessary(tag, null);
                    }
                }

                for (const [id, tags] of Object.entries(mappableTags.tagsByLinkTemplateDefinitionId)) {
                    for (const tag of tags) {
                        addMappingForTagIfNecessary(tag, id);
                    }
                }
            },
            [SET_RECIPIENT_CONTACT_FIELD]({ commit }, { recipientId, fieldType, fieldId, fixedValue }) {
                if (fieldType === ENVELOPE_RECIPIENT_CONTACT_FIELD_TYPE.ContactGroup) {
                    // TODO: Skip for now; may require additional DB column. Otherwise can't
                    // distinguish between contact and individual fields on reload
                    throw "Can't set recipient contact field via contact group; not yet implemented";
                } else {
                    commit(_COMMIT_RECIPIENT_CONTACT_INFO_SOURCE, { recipientId, fieldType, fieldId, fixedValue });
                }
            },
            [SET_RECIPIENT_ROUTING_ORDER]({ getters, commit }, { recipientId, routingOrder }) {
                // Update this recipient's routing order
                commit(_SET_SINGLE_RECIPIENT_ROUTING_ORDER, {
                    recipientId: recipientId,
                    routingOrder: routingOrder
                });

                consolidateParallelRoutingBuckets({ getters, commit });
            },
            [SET_RECIPIENT_ROUTING_ORDER_MULTIPLE]({ getters, commit }, orders) {
                // Update this recipient's routing order
                commit(_SET_MULTIPLE_RECIPIENT_ROUTING_ORDERS, orders);

                consolidateParallelRoutingBuckets({ getters, commit });
            },
            [SANITIZE_ENVELOPE_FOR_SAVE]({ getters, commit }) {
                // Helper functions
                function clearRecipientContactField(recipient, fieldType) {
                    commit(_COMMIT_RECIPIENT_CONTACT_INFO_SOURCE, {
                        recipientId: recipient.PublicId,
                        fieldType: fieldType,
                        fieldId: null,
                        fixedValue: null
                    });
                }

                const recipients = getters[RECIPIENT_LIST];

                // Sanitize recipients
                for (const recipient of recipients) {
                    // Clear unused contact info fields based on selected info type
                    switch (recipient.ContactInfoType) {
                        case ENVELOPE_RECIPIENT_CONTACT_INFO_TYPE.IndividualFields:
                            clearRecipientContactField(recipient, ENVELOPE_RECIPIENT_CONTACT_FIELD_TYPE.UserField);
                            clearRecipientContactField(recipient, ENVELOPE_RECIPIENT_CONTACT_FIELD_TYPE.FirstNameFixed);
                            clearRecipientContactField(recipient, ENVELOPE_RECIPIENT_CONTACT_FIELD_TYPE.LastNameFixed);
                            clearRecipientContactField(recipient, ENVELOPE_RECIPIENT_CONTACT_FIELD_TYPE.EmailFixed);
                            break;
                        case ENVELOPE_RECIPIENT_CONTACT_INFO_TYPE.UserField:
                            clearRecipientContactField(recipient, ENVELOPE_RECIPIENT_CONTACT_FIELD_TYPE.FirstNameField);
                            clearRecipientContactField(recipient, ENVELOPE_RECIPIENT_CONTACT_FIELD_TYPE.LastNameField);
                            clearRecipientContactField(recipient, ENVELOPE_RECIPIENT_CONTACT_FIELD_TYPE.EmailField);
                            clearRecipientContactField(recipient, ENVELOPE_RECIPIENT_CONTACT_FIELD_TYPE.FirstNameFixed);
                            clearRecipientContactField(recipient, ENVELOPE_RECIPIENT_CONTACT_FIELD_TYPE.LastNameFixed);
                            clearRecipientContactField(recipient, ENVELOPE_RECIPIENT_CONTACT_FIELD_TYPE.EmailFixed);
                            break;
                        case ENVELOPE_RECIPIENT_CONTACT_INFO_TYPE.Fixed:
                            clearRecipientContactField(recipient, ENVELOPE_RECIPIENT_CONTACT_FIELD_TYPE.FirstNameField);
                            clearRecipientContactField(recipient, ENVELOPE_RECIPIENT_CONTACT_FIELD_TYPE.LastNameField);
                            clearRecipientContactField(recipient, ENVELOPE_RECIPIENT_CONTACT_FIELD_TYPE.EmailField);
                            clearRecipientContactField(recipient, ENVELOPE_RECIPIENT_CONTACT_FIELD_TYPE.UserField);
                            break;
                        default:
                            throw `Unknown contact info type ${recipient.ContactInfoType}`;
                    }
                }
            },
            [APPLY_MAPPING_DEFAULTS]({ getters, commit, rootGetters }, { itemId, templateDefinitionId }) {
                const allTemplateTags =
                    rootGetters[DOCUMENT_TEMPLATE_TYPES.getters.getCurrentMappableSignatoryTagsFromDefinitionId](
                        templateDefinitionId
                    ).allTags;
                for (const mapping of getters[GET_MAPPINGS_BY_ITEM_ID](itemId).filter(
                    (m) => !m.MergeLinkTemplateId || m.MergeLinkTemplateId == templateDefinitionId
                )) {
                    const recipient = getters[GET_RECIPIENT_BY_ID](mapping.RecipientId);
                    const templateTag = allTemplateTags.find(
                        (tt) =>
                            tt.SignatoryTagNumber == mapping.SignatoryTagNumber &&
                            tt.TagType == mapping.TagType &&
                            tt.SignatoryLabel == recipient.Label
                    );
                    const templateTarget = templateTag?.SignatoryTargetObject?.PublicId;
                    if (
                        templateTarget &&
                        (mapping.DataTarget?.PublicId != templateTarget ||
                            templateTag.TagProperties?.Format != mapping.Properties?.Format)
                    ) {
                        const options = getters[GET_MAPPING_TARGET_OPTIONS_BY_MAPPING_ID](mapping.PublicId);
                        const type = options.find((option) => option.value === templateTarget)?.type;
                        commit(SET_MAPPING_FORMAT, {
                            mappingId: mapping.PublicId,
                            format: templateTag.TagProperties?.Format ?? 0
                        });
                        commit(SET_MAPPING_DATA_TARGET, {
                            mappingId: mapping.PublicId,
                            targetId: templateTarget,
                            targetType: type
                        });
                    }
                }
            }
        }
    };
}

function addRecipient({ state, commit }, { type, label }) {
    const recipient = getNewEnvelopeRecipient(type, label);
    commit(_INSERT_NEW_RECIPIENT, { newRecipient: recipient });

    if (state.EnvelopeDefinition.RoutingEnabled) {
        // For parallel routing, default to first routing group for new recipients.
        // TODO: Consider possibility of provider that doesn't support parallel routing
        const newRecipientRoutingOrder = 1;
        commit(_SET_SINGLE_RECIPIENT_ROUTING_ORDER, {
            recipientId: recipient.PublicId,
            routingOrder: newRecipientRoutingOrder
        });
    }
}

function removeRecipient({ state, getters, commit }, { recipientId }) {
    commit(_COMMIT_REMOVE_RECIPIENT, { recipientId: recipientId });

    // Need to update other recipient routing orders
    if (state.EnvelopeDefinition.RoutingEnabled) {
        consolidateParallelRoutingBuckets({ getters, commit });
    }
}

/**
 * Updating one recipient's order may remove the existence of an order "bucket", eg. if
 * all recipients in bucket 2 change to 3, then bucket 2 is removed, and everything that
 * was in bucket 3 should be changed to bucket 2.
 */
function consolidateParallelRoutingBuckets({ getters, commit }) {
    const recipients = getters[RECIPIENT_LIST];

    // Step 1: Create dictionary mapping routing order to the list of recipients with that
    // order
    const futureOrderBuckets = {};
    for (const recipient of recipients) {
        const recipientRoutingOrder = recipient.RoutingOrder;
        let recipientsWithOrder = futureOrderBuckets[recipientRoutingOrder];
        if (!recipientsWithOrder) {
            recipientsWithOrder = [];
            futureOrderBuckets[recipientRoutingOrder] = recipientsWithOrder;
        }
        recipientsWithOrder.push(recipient);
    }

    // Step 2: Turn that dictionary into an array, sorted by routing order
    const sortedFutureBuckets = Object.keys(futureOrderBuckets)
        .map((ro) => {
            const recipientsWithOrder = futureOrderBuckets[ro];
            return {
                plannedOrder: ro,
                recipientsWithOrder: recipientsWithOrder
            };
        })
        .sort((bucketA, bucketB) => numericCompare(bucketA.plannedOrder, bucketB.plannedOrder));

    // Step 3: Assign routing orders based on the order in the array. That way all
    // recipients will get the lowest actual order number possible while maintaining
    // the desired order
    for (let i = 0; i < sortedFutureBuckets.length; i++) {
        const bucket = sortedFutureBuckets[i];
        // Need to adjust RoutingOrder of every recipient in the bucket
        if (bucket.plannedOrder !== i + 1) {
            for (const recipient of bucket.recipientsWithOrder) {
                commit(_SET_SINGLE_RECIPIENT_ROUTING_ORDER, {
                    recipientId: recipient.PublicId,
                    routingOrder: i + 1
                });
            }
        }
    }
}

/**
 * NOT USED YET:
 * Handles updating routing order if parallel routing not supported
 */
// function handleSequentialRoutingChanged({ getters, commit }, { recipientId, routingOrder }) {

//     const targetRecipient = getters[GET_RECIPIENT_BY_ID](recipientId);
//     const oldRoutingOrder = targetRecipient.RoutingOrder;
//     const recipients = getters[RECIPIENT_LIST];

//     // May need to update other recipients' routing orders
//     // TODO: If parallel routing allowed, don't need to do this.
//     for (let i = 0; i < recipients.length; i++) {
//         let recipient = recipients[i];
//         if (recipient.PublicId === recipientId) {
//             commit(_SET_SINGLE_RECIPIENT_ROUTING_ORDER,
//                 {
//                     recipientId: recipient.PublicId,
//                     routingOrder: routingOrder
//                 });
//             continue;
//         }

//         let thisRoutingOrder = recipient.RoutingOrder;
//         // Current recipient is after new order but before old one -
//         // need to shift backwards
//         if (thisRoutingOrder >= routingOrder &&
//             thisRoutingOrder < oldRoutingOrder) {
//             commit(_SET_SINGLE_RECIPIENT_ROUTING_ORDER,
//                 {
//                     recipientId: recipient.PublicId,
//                     routingOrder: thisRoutingOrder + 1
//                 });
//         }
//         // Current recipient is after old order but before new one -
//         // need to shift forwards
//         else if (
//             thisRoutingOrder > oldRoutingOrder &&
//             thisRoutingOrder <= routingOrder) {
//             commit(_SET_SINGLE_RECIPIENT_ROUTING_ORDER,
//                 {
//                     recipientId: recipient.PublicId,
//                     routingOrder: thisRoutingOrder - 1
//                 });
//         }
//     }
// }

function hasExistingMapping(existingMappings, newMapping) {
    return existingMappings.some((mapping) => areMappingsEquivalent(mapping, newMapping));
}

function areMappingsEquivalent(m1, m2) {
    return (
        m1.ItemId === m2.ItemId &&
        m1.RecipientId === m2.RecipientId &&
        m1.TagType === m2.TagType &&
        m1.SignatoryTagNumber === m2.SignatoryTagNumber &&
        m1.MergeLinkTemplateId === m2.MergeLinkTemplateId
    );
}

function getEditEnvelopeTooltipAlertBinder() {
    return {
        dismissible: true,
        duration: ALERT_DURATION.indefinite,
        severity: ALERT_SEVERITY.info
    };
}

function changeDocumentTemplateItemSource(itemId, newSourceId, state, getters, rootGetters, commit, dispatch) {
    // Source ID of existing item changed
    const item = getters[GET_ITEM_BY_ID](itemId);
    if (item.Type !== ENVELOPE_ITEM_TYPE.DocumentTemplate) {
        return;
    }

    const exclusiveRecipients = getters[GET_RECIPIENTS_EXCLUSIVE_TO_ITEM](itemId);
    const newTemplateRecipientLabels =
        rootGetters[DOCUMENT_TEMPLATE_TYPES.getters.getCurrentSignatoryLabelSetFromDefinitionId](newSourceId);

    // Remove recipients that are exclusive to the old template and not on the new template
    for (const exclusiveRecipient of exclusiveRecipients) {
        if (!newTemplateRecipientLabels.has(exclusiveRecipient.Label)) {
            removeRecipient({ state, getters, commit }, { recipientId: exclusiveRecipient.PublicId });
        }
    }

    // Remove mappings
    const mappings = getters[GET_MAPPINGS_BY_ITEM_ID](itemId);
    for (const mapping of mappings) {
        commit(_REMOVE_MAPPING, { mappingId: mapping.PublicId });
    }

    // Add the new recipients and mappings
    commit(_SET_ITEM_DOCUMENT_TEMPLATE_DEFINITION_ID, { itemId: itemId, newDocTemplateId: newSourceId });
    initializeDocumentTemplateItem(item, state, rootGetters, getters, commit, dispatch);
}

function initializeDocumentTemplateItem(item, state, rootGetters, getters, commit, dispatch) {
    const templateDefinitionId = item.DocumentTemplateDefinitionId;
    const itemSignatoryLabelSet =
        rootGetters[DOCUMENT_TEMPLATE_TYPES.getters.getCurrentSignatoryLabelSetFromDefinitionId](templateDefinitionId);
    const existingLabelSet = getters[RECIPIENT_LABEL_SET];
    for (const label of itemSignatoryLabelSet) {
        if (!existingLabelSet.has(label)) {
            addRecipient(
                { state, getters, commit },
                {
                    type: ENVELOPE_RECIPIENT_TYPE.Signatory,
                    label: label
                }
            );
        }
    }

    dispatch(INITIALIZE_ITEM_MAPPINGS, { itemId: item.PublicId });
}
